// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { useState, useCallback, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SuperadminService } from "@/services/superadmin";
import { SecurityKPICards } from "@/components/superadmin/security/SecurityKPICards";
import { SecurityAlerts } from "@/components/superadmin/security/SecurityAlerts";
import { SessionTable } from "@/components/superadmin/security/SessionTable";
import { EmergencyAccessOverview } from "@/components/superadmin/security/EmergencyAccessOverview";

type SecurityTab = "overview" | "sessions" | "emergencyAccess";

export const Route = createFileRoute("/superadmin/security")({
  component: SecurityPage,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (["overview", "sessions", "emergencyAccess"].includes(
      search.tab as string,
    )
      ? search.tab
      : "overview") as SecurityTab,
  }),
});

function SecurityPage() {
  const { t } = useTranslation("superadmin");
  const { tab: initialTab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [tab, setTab] = useState<SecurityTab>(initialTab);

  // Session search & pagination state
  const [sessionSearch, setSessionSearch] = useState("");
  const [sessionPage, setSessionPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(sessionSearch);
      setSessionPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [sessionSearch]);

  const handleTabChange = useCallback(
    (newTab: string) => {
      const tabValue = newTab as SecurityTab;
      setTab(tabValue);
      navigate({
        search: { tab: tabValue },
        replace: true,
      });
    },
    [navigate],
  );

  // Queries
  const overviewQuery = useQuery({
    queryKey: ["superadmin", "securityOverview"],
    queryFn: () => SuperadminService.getSecurityOverview(),
    staleTime: 60000,
  });

  const sessionsQuery = useQuery({
    queryKey: ["superadmin", "activeSessions", sessionPage, debouncedSearch],
    queryFn: () =>
      SuperadminService.getActiveSessions({
        page: sessionPage,
        limit: 20,
        search: debouncedSearch || undefined,
      }),
    enabled: tab === "sessions" || tab === "overview",
  });

  const emergencyQuery = useQuery({
    queryKey: ["superadmin", "emergencyAccessOverview"],
    queryFn: () => SuperadminService.getEmergencyAccessOverview(),
    enabled: tab === "emergencyAccess",
    staleTime: 60000,
  });

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold">{t("security.title")}</h1>
        <p className="text-muted-foreground">{t("security.description")}</p>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="overview">
            {t("security.tabs.overview")}
          </TabsTrigger>
          <TabsTrigger value="sessions">
            {t("security.tabs.sessions")}
          </TabsTrigger>
          <TabsTrigger value="emergencyAccess">
            {t("security.tabs.emergencyAccess")}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <SecurityKPICards
            data={overviewQuery.data}
            isLoading={overviewQuery.isLoading}
          />
          <SecurityAlerts data={overviewQuery.data} />
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions">
          <SessionTable
            data={sessionsQuery.data}
            isLoading={sessionsQuery.isLoading}
            search={sessionSearch}
            onSearchChange={setSessionSearch}
            page={sessionPage}
            onPageChange={setSessionPage}
          />
        </TabsContent>

        {/* Emergency Access Tab */}
        <TabsContent value="emergencyAccess">
          <EmergencyAccessOverview
            data={emergencyQuery.data}
            isLoading={emergencyQuery.isLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
